import React, { useEffect, useMemo, useRef, useState } from "react";
import * as echarts from "echarts/core";
import { LineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  TitleComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsOption } from "echarts";

echarts.use([
  LineChart,
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  TitleComponent,
  CanvasRenderer,
]);

type RangeKey = "1D" | "5D" | "1M" | "6M" | "YTD" | "1Y" | "5Y" | "MAX";

type ApiResponse = {
  tz: { offsetSec: number; label: string };
  yAxis: {
    unit: "pct" | "price" | string;
    domain?: [number, number];
    ticks?: number[];
  };
  series: ApiSeries[];
};

type ApiEnvelope<T> = { data: T };

type AnyChartResponse =
  | ApiResponse
  | ApiEnvelope<ApiResponse>
  | Record<string, any>;

type ApiSeries = {
  id: string;
  exchange: string;
  name: string;
  currency: string;
  intervalSec: number;
  reference: { tsUtcMs: number; price: number };
  points: ApiPoint[];
  summary?: {
    latest?: ApiPoint;
    minChangePct?: number;
    maxChangePct?: number;
  };
};

type ApiPoint = {
  tsUtcMs: number;
  tsLocal?: string;
  price: number;
  changeAbs: number;
  changePct: number;
  volume: number | null;
  tzOffsetSec: number;
};

type StockSuggestResponse = {
  data: {
    meta?: {
      counts?: Record<string, number>;
    };
    tabs: Array<{
      key: string;
      label: string;
      items: StockSuggestItem[];
    }>;
  };
};

type StockSuggestItem = {
  assetType: string;
  currency?: string;
  exchange?: string;
  key: string; // e.g. "AAPL:NASDAQ"
  name?: string;
  symbol?: string;
  display?: {
    title?: string;
    subtitle?: string;
    price?: string;
    changePct?: string;
    trend?: "up" | "down" | string;
  };
  quote?: {
    price?: number;
    priceDecimals?: number;
    change?: number;
    changePct?: number;
    direction?: "up" | "down" | string;
  };
};

type Props = {
  /** e.g. [".INX:INDEXSP", "NVDA:NASDAQ", "TSLA:NASDAQ"] */
  initialSymbols?: string[];
  /** e.g. "5D" */
  initialRange?: RangeKey;
  /** Backend path, default: /api/v1/finance/chart */
  endpoint?: string;
  /** Optional locale for time/number formatting, default: "en-US" */
  locale?: string;
  className?: string;
};

const RANGE_OPTIONS: RangeKey[] = [
  "1D",
  "5D",
  "1M",
  "6M",
  "YTD",
  "1Y",
  "5Y",
  "MAX",
];

const DEFAULT_SYMBOLS = [".INX:INDEXSP", "NVDA:NASDAQ", "TSLA:NASDAQ"];

const SERIES_COLORS = [
  "#38bdf8", // cyan
  "#f43f5e", // rose
  "#f59e0b", // amber
  "#a78bfa", // violet
  "#34d399", // emerald
];

function shiftToApiTZ(tsUtcMs: number, tzOffsetSec: number) {
  // Represent the exchange-local time as a UTC timestamp so Intl formatting can use timeZone: 'UTC'.
  return tsUtcMs + tzOffsetSec * 1000;
}

function normalizeChartResponse(raw: AnyChartResponse): ApiResponse {
  // Backends may wrap payloads, e.g. { data: { ... } }
  const inner: any = (raw as any)?.data ?? raw;

  const series: any = inner?.series ?? (raw as any)?.series;
  if (!Array.isArray(series)) {
    // Provide a deterministic, debuggable error
    const keys = raw && typeof raw === "object" ? Object.keys(raw as any) : [];
    throw new Error(
      `Invalid chart response: expected array at (data.)series, got ${typeof series}. Top-level keys: ${keys.join(
        ", "
      )}`
    );
  }

  const tz = inner?.tz ?? (raw as any)?.tz ?? { offsetSec: 0, label: "UTC" };
  const yAxis = inner?.yAxis ?? (raw as any)?.yAxis ?? { unit: "pct" };

  return {
    tz,
    yAxis,
    series,
  } as ApiResponse;
}

function getKey(s: Pick<ApiSeries, "id" | "exchange">) {
  return `${s.id}:${s.exchange}`;
}

function formatNumber(
  n: number,
  locale: string,
  opts?: { maxFractionDigits?: number; minFractionDigits?: number }
) {
  const { maxFractionDigits = 2, minFractionDigits = 0 } = opts ?? {};
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: minFractionDigits,
  }).format(n);
}

function formatSigned(
  n: number,
  locale: string,
  opts?: { maxFractionDigits?: number; minFractionDigits?: number }
) {
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  return sign + formatNumber(Math.abs(n), locale, opts);
}

function formatPct(p: number, locale: string, digits = 2) {
  const sign = p > 0 ? "+" : p < 0 ? "-" : "";
  return (
    sign +
    formatNumber(Math.abs(p) * 100, locale, {
      maxFractionDigits: digits,
      minFractionDigits: digits,
    }) +
    "%"
  );
}

function rangeChangeLabel(range: RangeKey) {
  if (range === "1D") return "Today";
  return range;
}

async function fetchChart(
  endpoint: string,
  symbols: string[],
  range: RangeKey,
  signal?: AbortSignal
): Promise<ApiResponse> {
  const params = new URLSearchParams();
  params.set("symbols", symbols.join(","));
  params.set("range", range);

  const res = await fetch(`${endpoint}?${params.toString()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `HTTP ${res.status} ${res.statusText}${text ? `: ${text}` : ""}`
    );
  }

  // Some backends return JSON as a downloadable file (Content-Disposition: attachment) but it's still valid JSON.
  const text = await res.text();
  const raw = JSON.parse(text) as AnyChartResponse;
  return normalizeChartResponse(raw);
}

async function fetchStockSuggest(
  name: string,
  signal?: AbortSignal
): Promise<StockSuggestResponse> {
  const params = new URLSearchParams();
  params.set("name", name);
  // backend defaults exist; keep it minimal
  const res = await fetch(`/api/v1/finance/stock?${params.toString()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `HTTP ${res.status} ${res.statusText}${text ? `: ${text}` : ""}`
    );
  }
  const raw = (await res.json()) as StockSuggestResponse;
  return raw;
}

function normalizeTabs(resp: StockSuggestResponse | null | undefined) {
  const tabs = resp?.data?.tabs;
  if (!Array.isArray(tabs)) return [] as StockSuggestResponse["data"]["tabs"];
  return tabs;
}

export default function MarketIndexCard({
  initialSymbols = DEFAULT_SYMBOLS,
  initialRange = "5D",
  endpoint = "/api/v1/finance/chart",
  locale = "en-US",
  className,
}: Props) {
  const [symbols, setSymbols] = useState<string[]>(initialSymbols);
  const [range, setRange] = useState<RangeKey>(initialRange);
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search overlay state
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<string>("all");
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [suggestResp, setSuggestResp] = useState<StockSuggestResponse | null>(
    null
  );

  const chartDomRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<echarts.EChartsType | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Fetch
  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);

    fetchChart(endpoint, symbols, range, ac.signal)
      .then((resp) => {
        setData(resp);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Unknown error");
        setData(null);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });

    return () => ac.abort();
  }, [endpoint, symbols, range]);

  // Focus search input on open
  useEffect(() => {
    if (!isSearchOpen) return;
    const t = window.setTimeout(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [isSearchOpen]);

  // Esc to close search
  useEffect(() => {
    if (!isSearchOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsSearchOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isSearchOpen]);

  // Debounced suggest fetch
  useEffect(() => {
    if (!isSearchOpen) return;

    const q = searchQuery.trim();
    if (q.length < 1) {
      setSuggestResp(null);
      setSuggestError(null);
      setSuggestLoading(false);
      return;
    }

    const ac = new AbortController();
    setSuggestLoading(true);
    setSuggestError(null);

    const t = window.setTimeout(() => {
      fetchStockSuggest(q, ac.signal)
        .then((resp) => setSuggestResp(resp))
        .catch((e: unknown) => {
          if (ac.signal.aborted) return;
          setSuggestError(e instanceof Error ? e.message : "Unknown error");
          setSuggestResp(null);
        })
        .finally(() => {
          if (!ac.signal.aborted) setSuggestLoading(false);
        });
    }, 220);

    return () => {
      window.clearTimeout(t);
      ac.abort();
    };
  }, [isSearchOpen, searchQuery]);

  // Init chart once
  useEffect(() => {
    if (!chartDomRef.current) return;

    const dom = chartDomRef.current;
    const instance = echarts.init(dom, undefined, { renderer: "canvas" });
    chartRef.current = instance;

    const ro = new ResizeObserver(() => instance.resize());
    ro.observe(dom);

    return () => {
      ro.disconnect();
      instance.dispose();
      chartRef.current = null;
    };
  }, []);

  const orderedSeries = useMemo(() => {
    if (!data) return [] as ApiSeries[];

    const map = new Map<string, ApiSeries>();
    for (const s of data.series) map.set(getKey(s), s);

    const ordered: ApiSeries[] = [];

    for (const sym of symbols) {
      const [id, exchange] = sym.split(":");
      const key = `${id}:${exchange}`;

      const found = map.get(key);
      if (found) {
        ordered.push(found);
      }
    }

    return ordered;
  }, [data, symbols]);

  const primary = orderedSeries[0] ?? null;
  const tzOffsetSec = data?.tz?.offsetSec ?? 0;
  const tzLabel = data?.tz?.label ?? "UTC";

  const primaryLatest: ApiPoint | null = useMemo(() => {
    if (!primary) return null;
    return (
      primary.summary?.latest ??
      primary.points[primary.points.length - 1] ??
      null
    );
  }, [primary]);

  const asOfText = useMemo(() => {
    if (!primaryLatest) return "";

    const dt = new Date(shiftToApiTZ(primaryLatest.tsUtcMs, tzOffsetSec));
    const dtFmt = new Intl.DateTimeFormat(locale, {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });

    return `${dtFmt.format(dt)} ${tzLabel}${
      primary ? ` · ${primary.exchange}` : ""
    }`;
  }, [primary, primaryLatest, tzLabel, tzOffsetSec, locale]);

  const unit = data?.yAxis?.unit ?? "pct";

  const chartOption: EChartsOption = useMemo(() => {
    const yDomain = data?.yAxis?.domain;
    const yTicks = data?.yAxis?.ticks;

    const hasDomain = Array.isArray(yDomain) && yDomain.length === 2;
    const min = hasDomain ? yDomain![0] : undefined;
    const max = hasDomain ? yDomain![1] : undefined;

    const splitNumber = yTicks && yTicks.length >= 2 ? yTicks.length - 1 : 4;

    // Detect whether we should "compress" time gaps (e.g. overnight/weekend) by switching time -> category.
    // category axis keeps all points evenly spaced.
    const allTs = Array.from(
      new Set(
        orderedSeries.flatMap((s) =>
          (s.points ?? []).map((p) => shiftToApiTZ(p.tsUtcMs, tzOffsetSec))
        )
      )
    ).sort((a, b) => a - b);

    const baseIntervalSec =
      orderedSeries.length > 0
        ? Math.min(
            ...orderedSeries.map((s) =>
              typeof s.intervalSec === "number"
                ? s.intervalSec
                : Number.POSITIVE_INFINITY
            )
          )
        : Number.POSITIVE_INFINITY;
    const baseIntervalMs = Number.isFinite(baseIntervalSec)
      ? baseIntervalSec * 1000
      : 0;
    const GAP_THRESHOLD_MS = Math.max(baseIntervalMs * 3, 2 * 60 * 60 * 1000);

    let maxGap = 0;
    for (let i = 0; i + 1 < allTs.length; i++) {
      maxGap = Math.max(maxGap, allTs[i + 1] - allTs[i]);
    }

    // Enable category compression only when there's a clear gap and we're not in 1D.
    const useCategoryAxis = range !== "1D" && maxGap > GAP_THRESHOLD_MS;
    const categories = useCategoryAxis ? allTs.map(String) : [];

    const seriesOptions = orderedSeries.map((s, idx) => {
      const color = SERIES_COLORS[idx % SERIES_COLORS.length];
      const pts = s.points ?? [];

      const pointByTs = new Map<number, ApiPoint>();
      for (const p of pts) {
        pointByTs.set(shiftToApiTZ(p.tsUtcMs, tzOffsetSec), p);
      }

      const dataPoints = useCategoryAxis
        ? allTs.map((ts) => {
            const p = pointByTs.get(ts);
            if (!p) return { value: null, _ts: ts };
            const y = unit === "pct" ? p.changePct : p.price;
            return {
              value: y,
              _ts: ts,
              _price: p.price,
              _changeAbs: p.changeAbs,
              _changePct: p.changePct,
            };
          })
        : pts.map((p) => {
            const x = shiftToApiTZ(p.tsUtcMs, tzOffsetSec);
            const y = unit === "pct" ? p.changePct : p.price;
            return {
              value: [x, y],
              _ts: x,
              _price: p.price,
              _changeAbs: p.changeAbs,
              _changePct: p.changePct,
            };
          });

      return {
        name: s.name,
        type: "line" as const,
        showSymbol: false,
        connectNulls: true,
        symbol: "circle",
        emphasis: { focus: "series" as const },
        lineStyle: { width: 2, color },
        itemStyle: { color },
        data: dataPoints,
        // A flat baseline to make the % axis feel like the reference line in the mock.
        markLine:
          unit === "pct"
            ? {
                silent: true,
                symbol: ["none", "none"],
                label: { show: false },
                lineStyle: { width: 1, type: "solid", opacity: 0.35 },
                data: [{ yAxis: 0 }],
              }
            : undefined,
      };
    });

    const xAxisLabelFmt = (value: unknown) => {
      const ms = typeof value === "number" ? value : Number(value);
      if (!Number.isFinite(ms)) return "";

      const dt = new Date(ms);

      // Approximate per-range formatting.
      if (range === "1D") {
        return new Intl.DateTimeFormat(locale, {
          timeZone: "UTC",
          hour: "numeric",
          minute: "2-digit",
        }).format(dt);
      }

      return new Intl.DateTimeFormat(locale, {
        timeZone: "UTC",
        month: "2-digit",
        day: "2-digit",
      }).format(dt);
    };

    const xAxisOption = useCategoryAxis
      ? {
          type: "category",
          data: categories,
          boundaryGap: false,
          axisLine: { lineStyle: { color: "rgba(148, 163, 184, 0.35)" } },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: {
            color: "rgba(226, 232, 240, 0.7)",
            hideOverlap: true,
            formatter: (v: string) => xAxisLabelFmt(Number(v)),
          },
        }
      : {
          type: "time",
          axisLine: { lineStyle: { color: "rgba(148, 163, 184, 0.35)" } },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: {
            color: "rgba(226, 232, 240, 0.7)",
            formatter: xAxisLabelFmt as any,
            hideOverlap: true,
          },
        };

    const yAxisLabelFmt = (val: unknown) => {
      const v = typeof val === "number" ? val : Number(val);
      if (!Number.isFinite(v)) return "";
      if (unit === "pct") return `${(v * 100).toFixed(0)}%`;
      return formatNumber(v, locale, { maxFractionDigits: 0 });
    };

    const tooltipFormatter = (params: any) => {
      const items = Array.isArray(params) ? params : [params];
      const axisMs = Number(items?.[0]?.axisValue);
      const dt = new Date(axisMs);
      const header = new Intl.DateTimeFormat(locale, {
        timeZone: "UTC",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(dt);

      const lines = items
        .map((it: any) => {
          const d = it?.data ?? {};
          const price = d._price;
          const chgAbs = d._changeAbs;
          const chgPct = d._changePct;
          const marker = it.marker ?? "";

          const right =
            typeof price === "number" && typeof chgPct === "number"
              ? `${formatNumber(price, locale, {
                  maxFractionDigits: 2,
                })} (${formatPct(chgPct, locale)})`
              : typeof it.value === "number"
              ? unit === "pct"
                ? formatPct(it.value, locale)
                : formatNumber(it.value, locale, { maxFractionDigits: 2 })
              : typeof it.value?.[1] === "number"
              ? unit === "pct"
                ? formatPct(it.value[1], locale)
                : formatNumber(it.value[1], locale, { maxFractionDigits: 2 })
              : "";

          const extra =
            typeof chgAbs === "number" && typeof chgPct === "number"
              ? ` <span style="opacity:0.75">${formatSigned(chgAbs, locale, {
                  maxFractionDigits: 2,
                })}</span>`
              : "";

          return `
            <div style="display:flex; justify-content:space-between; gap:12px;">
              <div style="min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${marker}${it.seriesName}</div>
              <div style="font-variant-numeric: tabular-nums; white-space:nowrap;">${right}${extra}</div>
            </div>
          `;
        })
        .join("");

      return `
        <div style="font-size:12px;">
          <div style="margin-bottom:6px; font-weight:600;">${header} ${tzLabel}</div>
          ${lines}
        </div>
      `;
    };

    return {
      animation: false,
      grid: { left: 48, right: 18, top: 14, bottom: 32 },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line" },
        backgroundColor: "rgba(2, 6, 23, 0.92)",
        borderWidth: 0,
        padding: 10,
        textStyle: { color: "#e5e7eb" },
        formatter: tooltipFormatter,
      },
      xAxis: xAxisOption as any,
      yAxis: {
        type: "value",
        min,
        max,
        splitNumber,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: "rgba(226, 232, 240, 0.7)",
          formatter: yAxisLabelFmt as any,
        },
        splitLine: {
          show: true,
          lineStyle: { color: "rgba(148, 163, 184, 0.15)" },
        },
      },
      series: seriesOptions as any,
    } satisfies EChartsOption;
  }, [data, orderedSeries, range, tzOffsetSec, tzLabel, unit, locale]);

  // Apply option to chart
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.setOption(chartOption, {
      notMerge: true,
      lazyUpdate: true,
    });
  }, [chartOption]);

  const rows = useMemo(() => {
    return orderedSeries.map((s, idx) => {
      const latest = s.summary?.latest ?? s.points[s.points.length - 1];
      const color = SERIES_COLORS[idx % SERIES_COLORS.length];
      return {
        key: getKey(s),
        color,
        name: s.name,
        price: latest?.price ?? null,
        changeAbs: latest?.changeAbs ?? null,
        changePct: latest?.changePct ?? null,
      };
    });
  }, [orderedSeries]);

  const tabs = useMemo(() => normalizeTabs(suggestResp), [suggestResp]);
  const activeItems = useMemo(() => {
    if (tabs.length === 0) return [] as StockSuggestItem[];
    const tab = tabs.find((t) => t.key === activeTab) ?? tabs[0];
    const items = Array.isArray(tab?.items) ? tab.items : [];
    // Spec asks for stock add/remove; only allow selecting stocks.
    return items;
  }, [tabs, activeTab]);

  function openSearch() {
    setIsSearchOpen(true);
    setActiveTab("all");
    setSuggestError(null);
    // Keep existing text to allow quick refine; if empty, show placeholder.
  }

  function closeSearch() {
    setIsSearchOpen(false);
  }

  function addSymbol(sym: string) {
    const s = sym.trim();
    if (!s) return;
    setSymbols((prev) => {
      if (prev.includes(s)) return prev;
      return [...prev, s];
    });
  }

  function removeSymbol(sym: string) {
    setSymbols((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((x) => x !== sym);
      return next.length ? next : prev;
    });
  }

  function clearAllComparisons() {
    setSymbols((prev) => (prev.length ? [prev[0]] : prev));
  }

  const headerPrice = primaryLatest?.price ?? null;
  const headerPct = primaryLatest?.changePct ?? null;
  const headerAbs = primaryLatest?.changeAbs ?? null;

  const headerPillClass =
    headerPct == null
      ? ""
      : headerPct >= 0
      ? "bg-emerald-500/20 text-emerald-200"
      : "bg-rose-500/20 text-rose-200";

  return (
    <div
      className={
        "relative rounded-xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.03] p-5 text-white shadow-[0_14px_40px_-26px_rgba(0,0,0,0.8)] " +
        (className ?? "")
      }
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-semibold tracking-wide text-white/85">
            {primary?.name ?? "—"}
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <div className="text-4xl font-semibold tabular-nums text-white">
              {headerPrice == null
                ? "—"
                : formatNumber(headerPrice, locale, { maxFractionDigits: 2 })}
            </div>
            <div
              className={
                "rounded-full px-3 py-1 text-sm font-semibold tabular-nums " +
                headerPillClass
              }
            >
              {headerPct == null ? "—" : formatPct(headerPct, locale)}
            </div>
            <div className="text-sm tabular-nums text-white/65">
              {headerAbs == null
                ? ""
                : `${formatSigned(headerAbs, locale, {
                    maxFractionDigits: 2,
                  })} ${rangeChangeLabel(range)}`}
            </div>
          </div>
          <div className="mt-2 text-xs text-white/60">{asOfText}</div>
        </div>
      </div>

      {/* Range tabs */}
      <div className="mt-5 flex flex-wrap items-center gap-2 rounded-full border border-white/10 bg-white/5 p-1">
        {RANGE_OPTIONS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => {
              setRange(r);
              setData(null);
            }}
            className={
              "rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide transition-colors " +
              (r === range
                ? "bg-white/10 text-white"
                : "text-white/60 hover:text-white")
            }
          >
            {r}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-2">
        <div className="relative h-[260px] w-full">
          <div ref={chartDomRef} className="absolute inset-0" />
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-white/65">
              Loading…
            </div>
          ) : null}
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-rose-200">
              {error}
            </div>
          ) : null}
        </div>
      </div>

      {/* Comparison list */}
      <div className="mt-5 rounded-xl border border-white/10 bg-white/5 p-2">
        {rows.map((r, idx) => {
          const pillClass =
            r.changePct == null
              ? ""
              : r.changePct >= 0
              ? "bg-emerald-500/20 text-emerald-200"
              : "bg-rose-500/20 text-rose-200";

          return (
            <div
              key={r.key}
              className={
                "flex items-center gap-2 sm:gap-3 px-3 py-3 " +
                (idx === rows.length - 1 ? "" : "border-b border-white/10")
              }
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: r.color }}
              />

              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-white/85">
                  {r.name}
                </div>
              </div>
              <div className="hidden w-[88px] text-right text-sm tabular-nums text-white/85 sm:block">
                {r.price == null
                  ? "—"
                  : formatNumber(r.price, locale, { maxFractionDigits: 2 })}
              </div>
              <div className="hidden w-[86px] text-right text-sm tabular-nums text-white/60 sm:block">
                {r.changeAbs == null
                  ? ""
                  : formatSigned(r.changeAbs, locale, { maxFractionDigits: 2 })}
              </div>
              <div
                className={
                  "flex h-7 w-[72px] items-center justify-center rounded-full text-sm font-semibold tabular-nums sm:w-[86px] " +
                  pillClass
                }
              >
                {r.changePct == null ? "" : formatPct(r.changePct, locale)}
              </div>
              <button
                type="button"
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/65 hover:bg-white/10 disabled:opacity-50"
                onClick={() => removeSymbol(r.key)}
                disabled={symbols.length <= 1}
                title={
                  symbols.length <= 1 ? "Keep at least one symbol" : "Remove"
                }
                aria-label="Remove comparison"
              >
                ×
              </button>
            </div>
          );
        })}

        <div className="mt-2 flex items-center justify-between gap-3 px-3 py-3">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/85 hover:bg-white/10"
            onClick={openSearch}
          >
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/5 text-lg leading-none">
              +
            </span>
            Add comparison
          </button>

          <button
            type="button"
            className="text-sm text-white/60 hover:text-white"
            onClick={clearAllComparisons}
            disabled={symbols.length <= 1}
            aria-disabled={symbols.length <= 1}
            title={symbols.length <= 1 ? "Nothing to clear" : "Clear all"}
          >
            Clear all
          </button>
        </div>
      </div>

      {/* Search overlay */}
      {isSearchOpen ? (
        <div className="absolute inset-0 z-40">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={closeSearch}
            aria-label="Close search"
          />

          <div className="absolute left-5 right-5 top-5 rounded-2xl border border-white/15 bg-[#0a0c24]/90 shadow-[0_28px_70px_-40px_rgba(0,0,0,0.95)] backdrop-blur-xl">
            <div className="flex items-start justify-between gap-3 p-4">
              <div className="min-w-0 flex-1">
                <input
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search stocks (e.g. msft, apple)"
                  className="h-10 w-full rounded-full border border-white/15 bg-white/5 px-4 text-sm text-white/85 placeholder:text-white/45 outline-none focus:ring-2 focus:ring-cyan-400/30"
                />

                <div className="mt-3 flex items-center gap-2">
                  {tabs
                    .filter((t) => t.key === "all" || t.key === "stock")
                    .map((t) => {
                      const counts = suggestResp?.data?.meta?.counts;
                      const count =
                        typeof counts?.[t.key] === "number"
                          ? counts?.[t.key]
                          : undefined;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setActiveTab(t.key)}
                          className={
                            "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition-colors " +
                            (activeTab === t.key
                              ? "border-white/15 bg-white/10 text-white"
                              : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white")
                          }
                        >
                          <span>{t.label}</span>
                          {typeof count === "number" ? (
                            <span className="grid h-4 min-w-4 place-items-center rounded-full bg-white/10 px-1 text-[10px] text-white/70">
                              {count}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                </div>
              </div>

              <button
                type="button"
                onClick={closeSearch}
                className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/5 hover:bg-white/10"
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>

            <div className="border-t border-white/10">
              {suggestLoading ? (
                <div className="p-4 text-sm text-white/65">Loading…</div>
              ) : suggestError ? (
                <div className="p-4 text-sm text-rose-200">{suggestError}</div>
              ) : activeItems.length === 0 ? (
                <div className="p-4 text-sm text-white/60">
                  {searchQuery.trim() ? "No results." : "Type to search."}
                </div>
              ) : (
                <div className="max-h-[360px] overflow-auto">
                  {activeItems
                    .filter((it) => it.assetType === "stock")
                    .map((it) => {
                      const title =
                        it.display?.title ?? it.name ?? it.symbol ?? it.key;
                      const subtitle = it.display?.subtitle ?? it.key;
                      const priceText = it.display?.price;
                      const pct = it.quote?.changePct;
                      const direction =
                        it.quote?.direction ?? it.display?.trend;
                      const pillClass =
                        typeof pct === "number"
                          ? pct >= 0
                            ? "bg-emerald-500/20 text-emerald-200"
                            : "bg-rose-500/20 text-rose-200"
                          : "border border-white/10 bg-white/5 text-white/65";

                      const alreadyAdded = symbols.includes(it.key);

                      return (
                        <button
                          key={it.key}
                          type="button"
                          onClick={() => {
                            if (alreadyAdded) return;
                            addSymbol(it.key);
                            closeSearch();
                          }}
                          className={
                            "flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors " +
                            "hover:bg-white/5 " +
                            "border-b border-white/10 last:border-b-0 " +
                            (alreadyAdded ? "opacity-50" : "")
                          }
                          disabled={alreadyAdded}
                          title={alreadyAdded ? "Already added" : "Add"}
                        >
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-white/85">
                              {title}
                            </div>
                            <div className="truncate text-xs text-white/55">
                              {subtitle}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right text-sm tabular-nums text-white/85">
                              {priceText ?? ""}
                            </div>
                            <div
                              className={
                                "min-w-[86px] rounded-full px-3 py-1 text-right text-sm font-semibold tabular-nums " +
                                pillClass
                              }
                            >
                              {typeof pct === "number"
                                ? formatPct(pct, locale)
                                : direction === "up"
                                ? it.display?.changePct ?? ""
                                : it.display?.changePct ?? ""}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
